// =============================================================================
// ui/tabs/TradeTable.tsx — the trade table, shared by Trades and the Dashboard.
// -----------------------------------------------------------------------------
// EXTRACTED from Trades.tsx, where it was a local component.
//
// ── 🚨 THE OLD CLIENT CALLS ONE RENDERER FROM BOTH PLACES ──
//
// `renderTradeTable(recent, 'recent')` on the Dashboard, and the same function on
// the Trades tab. This client had drifted into TWO: eighteen columns on Trades,
// and a hand-rolled SIX on the Dashboard — date, time, sym, side, qty, net P&L.
//
// Josh caught it on a side-by-side. The Dashboard's version was missing account,
// time out, entry, exit, points, R, MAE, MFE, pattern, grade and discipline —
// most of what makes a row worth reading, and all of it already loaded.
//
// ⚠ TWO RENDERERS FOR ONE THING IS THE DRIFT THIS REBUILD EXISTS TO END. The
// second was not merely narrower: it formatted independently, so the two tables
// would have diverged on the next change to either. That is the 849-commits-in-
// one-file failure in miniature.
//
// ── `selectable` IS THE ONLY DIFFERENCE BETWEEN THE CALL SITES ──
//
// Trades owns bulk edit and needs the checkbox column and its select-all. The
// Dashboard is a read-only glance with neither, so it passes `selectable: false`
// and the column is not rendered AT ALL — not rendered-and-hidden, which would
// leave a dead cell in every row and a select-all that governs nothing.
//
// ⚠ Sorting is likewise opt-in. The Dashboard's six rows are the most recent six
// by definition; a sortable header there would offer to reorder a list whose
// whole meaning is its order.
// =============================================================================

import { useMemo } from 'react';
import type { TradeTableRow } from '../../io/sync/trades';
import { effectiveTradeDate, type DayBoundaryMode } from '../../selectors/population';

import type { Account } from '../../selectors/accounts';
import { clock, money, moneyPlain, num, pnlClass, unitValue  } from '../format';
import type { PnlUnit } from '../../selectors/pnlUnit';
import {
  TRADE_TABLE_COLUMNS as ALL_COLUMNS, grossPnlOf,
  type AccountLabeller, type PatternLabeller, type TradeColumn, type TradeSort,
} from './tradeColumns';
import { tradeDisciplinePct, type GaugeTrade } from '../../selectors/dashGauges';

export type { AccountLabeller } from './tradeColumns';

/**
 * The columns that exist ONLY in dollars.
 *
 * Gross differs from net by the commissions, and a commission does not move the
 * price — so neither figure has a points, ticks or R form. See the note in
 * `TradeTable` for why they are dropped rather than em-dashed.
 */
const DOLLAR_ONLY_COLUMNS: ReadonlySet<string> = new Set(['grossPnl', 'fees']);

export interface TableProps {
  rows: readonly TradeTableRow[];
  kind: 'annotated' | 'unannotated';
  mode: DayBoundaryMode;
  /**
   * ⚠ THE CONTRACT CATALOGUE IS GONE FROM THIS COMPONENT.
   *
   * It existed for exactly one thing — converting MAE/MFE points to dollars in
   * the excursion cells — and PD-5 removed that figure. A prop kept "in case"
   * is the dead-inventory rot in another form: it makes every caller look like
   * it is feeding the table something the table uses.
   *
   * The callers still load contracts; they need them for their own KPIs.
   */
  labelAccount: AccountLabeller;
  /**
   * Opens the trade form. **Omit it for a table whose rows do not open**, and
   * the row loses the pointer cursor, the hover highlight and the focus stop
   * with it.
   *
   * ⚠ 🚨 DO NOT PASS AN EMPTY FUNCTION TO SATISFY THE TYPE. The Dashboard did
   * exactly that: every row looked and behaved like a control — hand cursor,
   * keyboard focus, highlight — and clicking or pressing Enter did nothing at
   * all. A row that advertises an action it cannot perform is worse than a
   * plainly inert one, because the user concludes the app is broken rather than
   * that the feature is absent. Optional here so "no handler" is a state the
   * table can RENDER, not a lie a caller has to tell. See #45.
   */
  onOpen?: ((id: string) => void) | undefined;
  /**
   * Which layout to render. Defaults to the Trades tab's ACCOUNTING view
   * (`TRADE_TABLE_COLUMNS`), which is what this component always rendered.
   *
   * ⚠ The Dashboard must pass `TRADE_TABLE_COLUMNS_DEFAULT` (#45). Its Recent
   * Trades strip exists for the four REVIEW fields — setup, grade, discipline,
   * tags — and it was showing the accounting set instead: those four missing,
   * and Gross P&L and Commissions present, which beta deliberately keeps off
   * that screen.
   */
  columns?: readonly TradeColumn[];
  /**
   * The display unit, and one trade’s value in it.
   *
   * ⚠ A FUNCTION RATHER THAN THE CONTRACT CATALOGUE. The table has no other
   * reason to know what a tick size is, and three tabs render it — handing each
   * of them a catalogue to pass through would spread the conversion instead of
   * keeping it at the seam.
   *
   * ⚠ BOTH REQUIRED, deliberately. An optional `unit` defaulting to dollars is
   * exactly the silent-dollars failure this whole issue is about: a new caller
   * would compile, render, and quietly ignore the switch.
   */
  unit: PnlUnit;
  valueOf: (t: TradeTableRow) => number | null;
  /**
   * Resolves a trade's pattern NAME. Required only when the chosen layout has a
   * Pattern column; without it that cell renders empty rather than an id.
   */
  labelPattern?: PatternLabeller;
  /**
   * Bulk selection. Omit entirely for a read-only table — see the header: the
   * column is then not rendered at all rather than rendered empty.
   */
  selection?: {
    sort: TradeSort;
    onSort: (key: string) => void;
    selected: ReadonlySet<string>;
    onToggleRow: (id: string) => void;
    onToggleAll: (ids: readonly string[], on: boolean) => void;
  };
}

export function TradeTable(
  {
    rows, kind, mode, labelAccount, onOpen, selection, unit, valueOf,
    columns = ALL_COLUMNS, labelPattern,
  }: TableProps,
) {
  /**
   * ── 🚨 GROSS P&L AND COMMISSIONS ARE DOLLAR-ONLY, SO THEY LEAVE IN ANY OTHER
   *    UNIT (Josh, 2026-09-05) ──
   *
   * Gross differs from net by the COMMISSIONS, and a commission is a dollar
   * amount the broker charged — it does not move the price. So in points, ticks
   * or R there is no separate gross figure to state.
   *
   * The old handling rendered both columns anyway, with an em dash in Gross.
   * Beside a Net column that DID show a number, that reads as a bug rather than
   * as a distinction: **"if the Gross P&L can't show points, why does the Net
   * P&L show it? … it comes off as a bit asymmetric."** He was right — a column
   * that never has a value in the current unit should not be a column.
   *
   * ⚠ AND WE MEASURED THE ALTERNATIVE BEFORE REJECTING IT. Converting the money
   * to points instead — `netPnl ÷ ($/point × qty)` — was built, and run against
   * 447 real trades: it turns +110.93 pts into +8.88, because commissions are
   * 73% of gross ($2,568 of $3,513) on a book that trades small moves with size.
   * Points are the EXECUTION measure; folding cost into them makes them a money
   * figure wearing a points label. Backed out deliberately, not abandoned.
   *
   * ⚠ FILTERED HERE, not by each caller, so every table that follows the switch
   * follows this too — and a caller cannot forget.
   */
  const columnsInUnit = useMemo(
    () => (unit === 'usd' ? columns : columns.filter((c) => !DOLLAR_ONLY_COLUMNS.has(c.key))),
    [columns, unit],
  );
  const ids = rows.map((t) => String(t.id));
  const sel = selection?.selected;
  const allOn = !!sel && ids.length > 0 && ids.every((id) => sel.has(id));
  const someOn = !!sel && !allOn && ids.some((id) => sel.has(id));
  const sort = selection?.sort;
  return (
    <div className="table-wrap trade-table-card">
      <table className="table trades">
        <thead>
          <tr>
            {selection ? (
            <th className="sel-col">
              {/* ⚠ Select-all covers THIS TABLE's visible rows only. Reaching
                  past the filter would hand Delete a set the user cannot see —
                  and the two tables (Needs Review / Reviewed) are separate on
                  purpose, so ticking one must not silently take the other. */}
              <input
                type="checkbox"
                aria-label={`Select all ${kind === 'unannotated' ? 'needs-review' : 'reviewed'} trades`}
                checked={allOn}
                ref={(el) => { if (el) el.indeterminate = someOn; }}
                onChange={(e) => selection.onToggleAll(ids, e.target.checked)}
              />
            </th>
            ) : null}
            {columnsInUnit.map((c) => {
              // ⚠ A column is sortable only when the table IS sortable. On the
              // Dashboard the header must not offer to reorder six rows whose
              // whole meaning is "the most recent six".
              const sortable = c.sortable && !!selection;
              const active = sortable && sort?.key === c.key;
              const cls = [c.numeric ? 'num' : '', sortable ? 'sortable' : '', active ? 'sort-active' : '']
                .filter(Boolean).join(' ');
              return (
                <th
                  key={c.key}
                  className={cls}
                  data-sort-key={sortable ? c.key : undefined}
                  aria-sort={active ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  onClick={sortable ? () => selection?.onSort(c.key) : undefined}
                >
                  {c.label}
                  {sortable ? (
                    <span className="sort-arrow">{active ? (sort?.dir === 'asc' ? '▲' : '▼') : '▲▼'}</span>
                  ) : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <TradeRow
              key={String(t.id)} t={t} kind={kind} mode={mode}
              unit={unit} valueOf={valueOf}
              labelAccount={labelAccount} onOpen={onOpen}
              columns={columnsInUnit} labelPattern={labelPattern}
              select={selection ? {
                checked: selection.selected.has(String(t.id)),
                onToggle: selection.onToggleRow,
              } : null}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TradeRow(
  { t, kind, mode, labelAccount, onOpen, select, columns, labelPattern, unit, valueOf }:
  {
    t: TradeTableRow; kind: 'annotated' | 'unannotated'; mode: DayBoundaryMode;
    unit: PnlUnit;
    valueOf: (t: TradeTableRow) => number | null;
    labelAccount: AccountLabeller;
    columns: readonly TradeColumn[];
    labelPattern?: PatternLabeller | undefined;
    /** Absent on a table whose rows do not open — see the prop doc above.
        ⚠ `| undefined` explicitly: `exactOptionalPropertyTypes` is on, so a
        parent spreading a possibly-undefined handler cannot satisfy a bare `?`. */
    onOpen?: ((id: string) => void) | undefined;
    /** Null on a read-only table — the cell is omitted, not disabled. */
    select: { checked: boolean; onToggle: (id: string) => void } | null;
  },
) {
  const gross = grossPnlOf(t);
  // ⚠ `clickable` matches the old client, which puts the click on the ROW and
  // opens the trade form. Porting it as a "Review" button in a 17th column would
  // have been easier and wrong twice: a new column breaks the screenshot
  // comparison, and the old client's 17th column is a coach/delete PAIR.
  //
  // ⚠ 🚨 THAT COLUMN IS HALF COMING AND HALF NOT — DO NOT PORT IT WHOLESALE.
  // Josh's call, 2026-08-02: the **Coach Me** icon lands when the coach does;
  // the per-row **× delete** is never coming back. One click beside every row,
  // no confirm step in beta, and the rows are 24px apart. Deletion lives behind
  // the trade form and the bulk bar, both of which confirm. See PD-8.
  //
  // ⚠ 🚨 AND ALL THREE HALVES OF THE AFFORDANCE COME OFF TOGETHER WHEN THERE IS
  // NO HANDLER — the `clickable` class, the `tabIndex` and the key handler. Drop
  // only the click and the row still shows a hand and still takes focus; drop
  // only the class and the keyboard user still lands on a dead stop. See #45.
  const opens = typeof onOpen === 'function';
  const rowCls = [
    opens ? 'clickable' : '',
    kind === 'unannotated' || t.annotated === false ? 'unannotated' : '',
  ].filter(Boolean).join(' ');
  const open = opens ? () => onOpen(String(t.id)) : undefined;

  return (
    <tr
      className={rowCls}
      data-trade-id={String(t.id)}
      onClick={open}
      // ⚠ Keyboard reachable, which the old client's row is NOT. Added rather
      // than ported: it changes nothing visual, so it costs the parity comparison
      // nothing, and a table whose only way in is a mouse click is unusable for
      // anyone driving by keyboard.
      tabIndex={opens ? 0 : undefined}
      onKeyDown={open
        ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }
        : undefined}
    >
      {/* ⚠ `stopPropagation` — the row opens the editor on click, so without it
          ticking the checkbox would ALSO open the trade. Selecting 63 rows would
          mean dismissing 63 editors. */}
      {select ? (
        <td className="sel-col" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            aria-label={`Select trade ${String(t.id)}`}
            checked={select.checked}
            onChange={() => select.onToggle(String(t.id))}
          />
        </td>
      ) : null}
      {/* ── ⚠ CELLS ARE EMITTED BY COLUMN KEY, NOT IN A FIXED ORDER (#45) ──
          There are two layouts now — the Trades tab's accounting view and the
          default one every other caller uses — and they differ in ORDER as well
          as membership. A hard-coded row body renders the second set's headers
          over the first set's values, which is a table that looks right and is
          not. Each cell keeps the rendering it already had. */}
      {columns.map((c) => (
        <Cell
          key={c.key} col={c.key} t={t} mode={mode} gross={gross}
          unit={unit} value={valueOf(t)}
          labelAccount={labelAccount} labelPattern={labelPattern}
        />
      ))}
    </tr>
  );
}

/**
 * One cell, chosen by column key.
 *
 * ⚠ EVERY BRANCH IS THE RENDERING THAT CELL ALREADY HAD — this is a re-ordering,
 * not a redesign. The four review cells at the bottom are the new ones (#45).
 *
 * ⚠ AN UNKNOWN KEY RENDERS AN EMPTY CELL RATHER THAN NOTHING. Returning null
 * would shift every later column one place left under headers that still say
 * what they said, which is the exact failure this refactor exists to prevent.
 */
function Cell(
  { col, t, mode, gross, unit, value, labelAccount, labelPattern }: {
    col: string;
    t: TradeTableRow;
    mode: DayBoundaryMode;
    gross: number | null;
    unit: PnlUnit;
    /** This trade in `unit`. Null where it has none — no stop, no R. */
    value: number | null;
    labelAccount: AccountLabeller;
    labelPattern?: PatternLabeller | undefined;
  },
) {
  switch (col) {
    // The EFFECTIVE date; `title` keeps the raw calendar date reachable, which
    // is how a user checks WHY an evening trade sits on the next day.
    case 'date': return <td title={t.date ?? ''}>{effectiveTradeDate(t, mode)}</td>;
    case 'time': return <td>{clock(t.timeIn)}</td>;
    case 'timeout': return <td>{clock(t.timeOut)}</td>;
    case 'symbol': return (
      <td>
        <b>{t.contract ?? ''}</b>
        {t.syntheticClose ? (
          <span className="tag gold" title="Synthetic close — manually entered exit"> syn</span>
        ) : null}
      </td>
    );
    case 'account': return (
      <td className="muted account-cell" title={t.sourceAccount ?? ''}>{labelAccount(t)}</td>
    );
    case 'side': return <td className={t.side === 'long' ? 'green' : 'red'}>{t.side ?? ''}</td>;
    case 'qty': return <td className="num">{t.qty ?? 0}</td>;
    // Entry/exit render EMPTY rather than an em dash when absent, matching the
    // old client: a missing price on an open position is not a null result.
    case 'entry': return <td className="num">{t.entry != null ? num(t.entry, 2) : ''}</td>;
    case 'exit': return <td className="num">{t.exit != null ? num(t.exit, 2) : ''}</td>;
    case 'r': return <td className="num">{num(t.rrRealized, 2)}</td>;
    case 'mae': return <ExcursionCell points={t.maePoints} tone="red" />;
    case 'points': return <td className="num">{num(t.points, 2)}</td>;
    case 'mfe': return <ExcursionCell points={t.mfePoints} tone="green" />;
    // ⚠ ALWAYS DOLLARS, AND ALWAYS REACHED IN DOLLARS. This used to render an
    // em dash in every other unit; the column is now filtered out entirely
    // above, which is the honest version of the same idea — see the note there.
    case 'grossPnl': return (
      <td className={`num ${pnlClass(gross)}`}>{money(gross)}</td>
    );
    // ⚠ UNSIGNED — and the old client was CHANGED TO MATCH (D58 resolved). It
    // rendered a $2 commission as "+$2.00", which reads as a credit on a cost.
    // Josh called it: fix it in both, so the parity comparison stays clean and
    // neither client shows a cost with a plus in front of it.
    // ⚠ ALWAYS DOLLARS. A commission is what the broker charged; it has no
    // points value, and beta keeps fees in dollars for the same reason.
    case 'fees': return <td className="num">{moneyPlain(t.fees ?? 0)}</td>;
    case 'pnl': return (
      // ⚠ The COLOUR still comes from the dollar sign, not the unit value.
      // A trade is a winner or a loser by its money; in ticks an uncatalogued
      // contract has no value at all, and coluring that grey would make an
      // ordinary losing trade look like a data fault.
      <td className={`num ${pnlClass(t.netPnl)}`}>{unitValue(value, unit)}</td>
    );

    // ── the four review columns ──────────────────────────────────────────────
    case 'pattern': return (
      <td className="muted trunc" title={labelPattern ? labelPattern(t) : ''}>
        {labelPattern ? labelPattern(t) : ''}
      </td>
    );
    case 'grade': return <td>{t.grade ? <span className="tag">{t.grade}</span> : ''}</td>;
    case 'disc': {
      // ⚠ EM DASH FOR AN UNREVIEWED TRADE, never 0% and never 100%. The breach
      // flags are stored as "absent means it did not happen", so an untouched
      // import has six absent flags — printing 100% there would credit the
      // trader with discipline on a trade nobody has looked at.
      const d = tradeDisciplinePct(t as unknown as GaugeTrade);
      return <td className="num">{d == null ? '—' : `${Math.round(d)}%`}</td>;
    }
    case 'tags': return (
      <td className="trunc" title={(t.mistakeTags ?? []).join(', ')}>
        {(t.mistakeTags ?? []).map((tag) => (
          <span key={tag} className="tag red">{tag}</span>
        ))}
      </td>
    );

    default: return <td />;
  }
}

/**
 * MAE / MFE: the points figure, plus what it was worth.
 *
 * ⚠ The dollar value is not decoration. Points are not comparable across
 * instruments — 12 points of adverse excursion is $600 on ES and $24 on MNQ — so
 * a points-only column understates risk on micros and overstates it on the
 * fulls. The old client shows both for that reason.
 */
/**
 * MAE / MFE, in POINTS only.
 *
 * ── ⚠ THE DOLLAR FIGURE WAS REMOVED ON PURPOSE — PD-5, Josh's call ──
 *
 * Both the old client and the first version of this cell printed a muted dollar
 * value beside the points ("3.25 +$13"). Two numbers in one narrow, right-
 * aligned cell, on a table that already has sixteen columns, and the pair
 * squeezed rather than read.
 *
 * ⚠ NOTHING WAS LOST FROM THE MODEL. `excursionValue` still converts, and the
 * dollar figure is still on screen where it has room to be read: the trade
 * detail modal's Risk tab, and the Excursion analytics tab. This is a display
 * decision about ONE cell, not a narrowing of what the client knows.
 *
 * ⚠ AND IT APPLIES EVERYWHERE THE TABLE APPEARS — Trades, the Dashboard's
 * Recent Trades and the Day Overview. One component rendering its own MAE
 * column two different ways depending on the tab is the asymmetry this change
 * exists to remove.
 */
function ExcursionCell(
  { points, tone }: { points: number | null; tone: 'red' | 'green' },
) {
  if (points == null) return <td className="num"><span className="muted">—</span></td>;
  return <td className="num"><span className={tone}>{num(points, 2)}</span></td>;
}

/**
 * Account name → external id → "(unnamed)", falling back to whatever the broker
 * export called it. Ported from `tradeAccountLabel`.
 *
 * ⚠ The fallback chain exists because an unassigned trade is exactly the one a
 * user needs to identify. An em dash there is the least useful thing to show.
 */
export function makeAccountLabeller(accounts: readonly Account[]): AccountLabeller {
  // ⚠ Keyed on the STRINGIFIED id. `AccountId` is a branded type and
  // `Trade.accountId` is not branded, so a map keyed on the brand cannot be
  // looked up from a trade without a cast — and the cast is the part that would
  // go wrong silently if the two ever diverged in shape rather than in nominal
  // type. Comparing the underlying strings is what actually has to hold.
  const byId = new Map(accounts.map((a) => [String(a.id), a]));
  return (t) => {
    const a = t.accountId != null ? byId.get(String(t.accountId)) : undefined;
    if (a) return a.name || a.externalId || '(unnamed)';
    return t.sourceAccount || '—';
  };
}
